import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useToast } from "../../../../../Hooks/UseToast";
import { getSemana, getOffsetSemanaDesdeLunes } from "../../../common/utils/semana";
import { getProveedores } from "../../proveedores/service/proveedoresService";
import { getMenus } from "../../menus/service/menusService";
import {
  cargarSemanal,
  actualizarFeriado,
  eliminarDisponibilidad,
} from "../service/disponibilidadService";
import { useDisponibilidadSemana } from "../hooks/useDisponibilidadSemana";
import SemanaNavigator from "../../../empleado/components/SemanaNavigator";
import CargaSemanalGrid from "../components/CargaSemanalGrid";
import DisponibilidadTable from "../components/DisponibilidadTable";
import ImportarMenuModal from "../modals/ImportarMenuModal";
import ImportarMenuBonFoodModal from "../modals/ImportarMenuBonFoodModal";

const DisponibilidadPage = () => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [proveedores, setProveedores] = useState([]);
  const [selectedProveedorId, setSelectedProveedorId] = useState("");
  const [menus, setMenus] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(false);
  const [matrix, setMatrix] = useState({});
  const [feriados, setFeriados] = useState({});
  const [saving, setSaving] = useState(false);
  const [modalImportarAbierto, setModalImportarAbierto] = useState(false);
  const [modalImportarBonFoodAbierto, setModalImportarBonFoodAbierto] = useState(false);
  const { showToast, Toast } = useToast();

  const semana = useMemo(() => getSemana(weekOffset), [weekOffset]);

  const {
    data: existentes,
    loading: loadingExistentes,
    refresh: refreshExistentes,
  } = useDisponibilidadSemana(semana.desde, semana.hasta);

  useEffect(() => {
    getProveedores()
      .then((res) => setProveedores((res || []).filter((p) => Number(p.activo) === 1)))
      .catch(() => setProveedores([]));
  }, []);

  useEffect(() => {
    if (!selectedProveedorId) {
      setMenus([]);
      return;
    }
    setLoadingMenus(true);
    getMenus(selectedProveedorId)
      .then((res) => setMenus((res || []).filter((m) => Number(m.activo) === 1)))
      .catch(() => setMenus([]))
      .finally(() => setLoadingMenus(false));
  }, [selectedProveedorId]);

  useEffect(() => {
    const nuevaMatrix = {};
    const nuevosFeriados = {};
    existentes.forEach((e) => {
      if (Number(e.id_proveedor) === Number(selectedProveedorId)) {
        nuevaMatrix[`${e.fecha}|${e.id_menu}`] = true;
      }
      if (Number(e.es_feriado) === 1) nuevosFeriados[e.fecha] = true;
    });
    setMatrix(nuevaMatrix);
    setFeriados(nuevosFeriados);
  }, [existentes, selectedProveedorId]);

  const toggleCelda = (fecha, idMenu) => {
    if (feriados[fecha]) return;
    const key = `${fecha}|${idMenu}`;
    setMatrix((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleFeriado = (fecha) => {
    const activando = !feriados[fecha];
    setFeriados((prev) => ({ ...prev, [fecha]: activando }));
    if (activando) {
      setMatrix((prev) => {
        const next = { ...prev };
        menus.forEach((m) => {
          next[`${fecha}|${m.id}`] = true;
        });
        return next;
      });
    }
  };

  const handleGuardar = async () => {
    const payload = [];
    Object.entries(matrix).forEach(([key, checked]) => {
      if (!checked) return;
      const [fecha, idMenuStr] = key.split("|");
      payload.push({
        id_menu: Number(idMenuStr),
        fecha,
        es_feriado: feriados[fecha] ? 1 : 0,
      });
    });

    if (!payload.length) {
      showToast("Marcá al menos un menú disponible para guardar.", "warning");
      return;
    }

    setSaving(true);
    try {
      const res = await cargarSemanal(payload);
      showToast(res.message || "Menú semanal cargado correctamente.", "success");
      await refreshExistentes();
    } catch (err) {
      showToast(err.message || "Error al guardar la disponibilidad.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFeriadoExistente = async (id, nuevoValor) => {
    try {
      await actualizarFeriado(id, nuevoValor);
      showToast("Actualizado.", "success");
      refreshExistentes();
    } catch (err) {
      showToast(err.message || "Error al actualizar.", "error");
    }
  };

  const handleEliminar = async (id) => {
    try {
      await eliminarDisponibilidad(id);
      showToast("Disponibilidad eliminada.", "info");
      refreshExistentes();
    } catch (err) {
      showToast(err.message || "Error al eliminar.", "error");
    }
  };

  const handleImportado = (resultado, idProveedorImportado) => {
    const lunes = resultado?.semana?.lunes;
    if (lunes) setWeekOffset(getOffsetSemanaDesdeLunes(lunes));
    if (idProveedorImportado) setSelectedProveedorId(idProveedorImportado);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
          Carga del menú semanal
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Elegí la semana y el proveedor, marcá qué menú está disponible cada día y guardá
          para desbloquear la elección de los empleados.
        </Typography>
      </Box>

      <SemanaNavigator
        desde={semana.desde}
        hasta={semana.hasta}
        esSemanaActual={semana.esSemanaActual}
        onPrev={() => setWeekOffset((o) => o - 1)}
        onNext={() => setWeekOffset((o) => o + 1)}
        onHoy={() => setWeekOffset(0)}
      />

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <Select
            displayEmpty
            value={selectedProveedorId}
            onChange={(e) => setSelectedProveedorId(e.target.value)}
            sx={{ bgcolor: "white" }}
          >
            <MenuItem value="">
              <em>Elegí un proveedor</em>
            </MenuItem>
            {proveedores.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="success"
          startIcon={<LockOpenIcon />}
          disabled={!selectedProveedorId || saving}
          onClick={handleGuardar}
          sx={{ borderRadius: 3, textTransform: "none", fontWeight: 700 }}
        >
          Guardar y desbloquear
        </Button>

        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => setModalImportarAbierto(true)}
          sx={{ borderRadius: 3, textTransform: "none", fontWeight: 600 }}
        >
          Importar desde Excel
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          startIcon={<UploadFileIcon />}
          onClick={() => setModalImportarBonFoodAbierto(true)}
          sx={{ ml: "auto", borderRadius: 3, textTransform: "none", fontWeight: 600 }}
        >
          Importar BonFood
        </Button>
      </Box>

      {selectedProveedorId && loadingMenus && (
        <Skeleton variant="rounded" height={180} sx={{ borderRadius: 3 }} />
      )}

      {selectedProveedorId && !loadingMenus && (
        <CargaSemanalGrid
          dias={semana.dias}
          menus={menus}
          matrix={matrix}
          feriados={feriados}
          onToggleCelda={toggleCelda}
          onToggleFeriado={toggleFeriado}
        />
      )}

      <Divider />

      <DisponibilidadTable
        data={existentes}
        loading={loadingExistentes}
        onToggleFeriado={handleToggleFeriadoExistente}
        onEliminar={handleEliminar}
      />

      <ImportarMenuModal
        open={modalImportarAbierto}
        onClose={() => setModalImportarAbierto(false)}
        proveedores={proveedores}
        proveedorInicial={selectedProveedorId}
        onImportado={handleImportado}
        showToast={showToast}
      />

      <ImportarMenuBonFoodModal
        open={modalImportarBonFoodAbierto}
        onClose={() => setModalImportarBonFoodAbierto(false)}
        proveedores={proveedores}
        proveedorInicial={selectedProveedorId}
        onImportado={handleImportado}
        showToast={showToast}
      />

      {Toast}
    </Box>
  );
};

export default DisponibilidadPage;
